import * as yup from 'yup';
import moment from 'moment';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { VisibilityOutlined, VisibilityOffOutlined } from '@mui/icons-material';

import InputField from '../Input';
import Button from '../../common/Button';
import { postRequest, showMessage } from '../../../config';
import { toggleModal } from '../../../reducers/modalReducer';


const changePasswordSchema = yup.object().shape({
    currentPassword: yup.string().required('Current password is required'),
    newPassword: yup
        .string()
        .required('New password is required')
        .min(6, 'Password must be at least 6 characters'),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
        .required('Confirm password is required'),
});

const ChangePassword = ({ onSubmitData, userData }) => {

    // Hooks and vars
    const dispatch = useDispatch()
    const [passwordVisibility, setPasswordVisibility] = useState({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
    });

    const togglePasswordVisibility = (field) => {
        setPasswordVisibility((prevState) => ({
            ...prevState,
            [field]: !prevState[field],
        }));
    };

    const { register, handleSubmit, watch, reset, formState: { errors }, } = useForm({ resolver: yupResolver(changePasswordSchema), });

    const watchFields = watch(['currentPassword', 'newPassword', 'confirmPassword']);

    const onSubmit = async (data) => {
        try {
            const res = await postRequest(`/user/changePassword`, {
                oldPassword: data.currentPassword,
                newPassword: data.newPassword,
            });
            if (!res || res["code"] != 200) return;
            showMessage(res["message"]);
            reset()
            dispatch(toggleModal({ open: 'passwordUpdatedSuccessfully', historyData: { message: 'Your password has been updated successfully.' } }))
        } catch (e) {
            showMessage(e["message"]);
        }
    };

    const renderPasswordField = (id, label, error, index) => (
        <InputField
            id={id}
            type={passwordVisibility[id] ? 'text' : 'password'}
            {...register(id)}
            error={error}
            placeholder={`Enter ${label}`}
            prefixIcon={
                <svg
                    className="pa c2"
                    style={{
                        left: '15px',
                        marginTop: '0.8rem',
                        color: watchFields[index] ? '#000' : '#505050',
                    }}
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M13.0078 12.8672C13.3961 12.8672 13.7109 12.5524 13.7109 12.1641C13.7109 11.7757 13.3961 11.4609 13.0078 11.4609C12.6195 11.4609 12.3047 11.7757 12.3047 12.1641C12.3047 12.5524 12.6195 12.8672 13.0078 12.8672Z" fill={`${watchFields[index] ? '#000' : '#505050'}`} />
                    <path d="M16.1719 12.7266C16.5602 12.7266 16.875 12.4118 16.875 12.0234V9.42188C16.875 7.87106 15.6133 6.60938 14.0625 6.60938H13.2174V4.12966C13.2174 1.85256 11.3249 0 8.9987 0C6.67248 0 4.77995 1.85256 4.77995 4.12966V6.60938H3.9375C2.38669 6.60938 1.125 7.87106 1.125 9.42188V15.1875C1.125 16.7383 2.38669 18 3.9375 18H14.0625C15.6133 18 16.875 16.7383 16.875 15.1875C16.875 14.7992 16.5602 14.4844 16.1719 14.4844C15.7835 14.4844 15.4688 14.7992 15.4688 15.1875C15.4688 15.9629 14.8379 16.5938 14.0625 16.5938H3.9375C3.16209 16.5938 2.53125 15.9629 2.53125 15.1875V9.42188C2.53125 8.64647 3.16209 8.01562 3.9375 8.01562H14.0625C14.8379 8.01562 15.4688 8.64647 15.4688 9.42188V12.0234C15.4688 12.4118 15.7835 12.7266 16.1719 12.7266ZM11.8112 6.60938H6.1862V4.12966C6.1862 2.62796 7.44789 1.40625 8.9987 1.40625C10.5495 1.40625 11.8112 2.62796 11.8112 4.12966V6.60938Z" fill={`${watchFields[index] ? '#000' : '#505050'}`} />
                    <path d="M7.70312 12.8672C8.09145 12.8672 8.40625 12.5524 8.40625 12.1641C8.40625 11.7757 8.09145 11.4609 7.70312 11.4609C7.3148 11.4609 7 11.7757 7 12.1641C7 12.5524 7.3148 12.8672 7.70312 12.8672Z" fill={`${watchFields[index] ? '#000' : '#505050'}`} />
                    <path d="M5.0625 12.8672C5.45083 12.8672 5.76562 12.5524 5.76562 12.1641C5.76562 11.7757 5.45083 11.4609 5.0625 11.4609C4.67417 11.4609 4.35938 11.7757 4.35938 12.1641C4.35938 12.5524 4.67417 12.8672 5.0625 12.8672Z" fill={`${watchFields[index] ? '#000' : '#505050'}`} />
                    <path d="M10.3359 12.8672C10.7243 12.8672 11.0391 12.5524 11.0391 12.1641C11.0391 11.7757 10.7243 11.4609 10.3359 11.4609C9.94761 11.4609 9.63281 11.7757 9.63281 12.1641C9.63281 12.5524 9.94761 12.8672 10.3359 12.8672Z" fill={`${watchFields[index] ? '#000' : '#505050'}`} />
                </svg>
            }
            afterIcon={
                passwordVisibility[id] ? (
                    <VisibilityOffOutlined
                        className="pa c2 cursor-pointer"
                        style={{
                            right: '15px',
                            marginTop: '0.8rem',
                            color: watchFields[index] ? '#000' : '#505050',
                        }}
                        onClick={() => togglePasswordVisibility(id)}
                    />
                ) : (
                    <VisibilityOutlined
                        className="pa c2 cursor-pointer"
                        style={{
                            right: '15px',
                            marginTop: '0.8rem',
                            color: watchFields[index] ? '#000' : '#505050',
                        }}
                        onClick={() => togglePasswordVisibility(id)}
                    />
                )
            }
        />
    );

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            {/* <h2 className="c15 fw600 fs1rem mt2rem">Change Password</h2> */}
            <div className="w100 pr">
                {renderPasswordField('currentPassword', 'Current Password', errors.currentPassword, 0)}
            </div>
            <div className="df aic jcsb mt0-5rem">
                <div className="w50 pr mr1rem">
                    {renderPasswordField('newPassword', 'New Password', errors.newPassword, 1)}
                </div>
                <div className="w50 pr">
                    {renderPasswordField('confirmPassword', 'Confirm Password', errors.confirmPassword, 2)}
                </div>
            </div>
            <div className="mt1rem df jcc">
                <Button type="submit" style={{ width: '250px', maxHeight: "45px" }} customClassName="c1" btnType={'warning'} text={"Confirm Password"} />
            </div>
        </form>
    );
};

export default ChangePassword;
